import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useWebSocket } from '../../connections/WebSocketContext.js';
import LoadMeeting from './LoadMeeting.js';
import TranscriptTabs from './TranscriptTabs.js';
import MeetingObjectives from './MeetingObjectives.js';
import MeetingInsights from './MeetingInsights.js';
import ParticipantHover from '../sharing/ParticipantHover.js';
import SharingPanel from '../sharing/SharingPanel.js';
import TimelinePanel from './TimelinePanel.js';
import TrainingPlayer from './training/TrainingPlayer.js';

import { meetingCoordinatorService } from '../../api/api_meeting_coordinator.js';
import linkLogo from '../../assets/liminal-logo-signin.svg';
import linkIcon from '../../assets/icon128.png';

function Meeting({ isViewerMode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { instanceId: urlInstanceId } = useParams();
  
  /* ------------------------------------------------------------------
   * UTILITY FUNCTIONS
   * ----------------------------------------------------------------*/
  const getDisplayName = (meetingData) => {
    if (!meetingData) return '';
    
    // console.log('🔍 getDisplayName called with meetingData:', meetingData);
    // console.log('🔍 meetingData.entity?.name:', meetingData.entity?.name);
    // console.log('🔍 meetingData.name:', meetingData.name);
    // console.log('🔍 meetingData.data?.entity?.name:', meetingData.data?.entity?.name);
    // console.log('🔍 meetingData.data?.name:', meetingData.data?.name);
    
    // Get the meeting name from various possible locations
    const meetingName = meetingData.entity?.name || 
                       meetingData.name || 
                       meetingData.data?.entity?.name ||
                       meetingData.data?.name ||
                       'Meeting';
    
    console.log('🔍 Final meetingName:', meetingName);
    
    // Get the short_id from various possible locations in the data structure
    const shortId = meetingData.short_id || 
                   meetingData.instance?.short_id || 
                   meetingData.data?.short_id ||
                   meetingData.data?.instance?.short_id ||
                   meetingData.instance_id?.substring(0, 8) || // fallback to first 8 chars of instance_id
                   meetingData.meetingId?.substring(0, 8) || // fallback to first 8 chars of meetingId
                   meetingData._id?.substring(0, 8) || // fallback to first 8 chars of _id
                   meetingData.id?.substring(0, 8); // fallback to first 8 chars of id
    
    // If the name looks like a UUID (only hex characters and hyphens), use a generic name
    if (/^[a-f0-9-]+$/i.test(meetingName)) {
      return `Meeting (${shortId})`;
    }
    
    // Otherwise use the actual name with short ID
    return `${meetingName} (${shortId})`;
  };
  
  /* ------------------------------------------------------------------
   * LOCAL STATE
   * ----------------------------------------------------------------*/
  const [isRecording, setIsRecording] = useState(false);  // recording flag
  const [extensionError, setExtensionError] = useState(null);
  const [meetingData, setMeetingData] = useState(null);  // store loaded meeting data
  const [meetingObjectives, setMeetingObjectives] = useState(null);  // store loaded meeting data
  const [meetingStarted, setMeetingStarted] = useState(false);  // meeting running state
  const [meetingStopped, setMeetingStopped] = useState(false);  // meeting stopped state
  const [meetingConfig, setMeetingConfig] = useState(null);  // meeting configuration from coordinator
  const [isLoading, setIsLoading] = useState(false);  // loading state for coordinator operations
  const [isLoadingMeetingDetails, setIsLoadingMeetingDetails] = useState(false);  // loading state for meeting details API call
  const [coordinatorError, setCoordinatorError] = useState(null);
  const [meetingClosed, setMeetingClosed] = useState(false);  // meeting closed state
  const [isStartingNewMeeting, setIsStartingNewMeeting] = useState(false);  // new meeting starting state
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);  // show copied message state
  const [participants, setParticipants] = useState(null);  // store participant data
  const [showSharingPanel, setShowSharingPanel] = useState(false);  // sharing panel state
  const [showTimelinePanel, setShowTimelinePanel] = useState(false);  // timeline panel state

  /* ------------------------------------------------------------------
   *  WEBSOCKET CONTEXT (custom hook)
   * ----------------------------------------------------------------*/
  const {
    isConnected,
    // New data from WebSocket
    formatTranscription,
    rawTranscription,
    interimTranscription,
    objectives,
    suggestions,
    questionList,
    actionItems,
    emphasisPoints,
    sessionSentiment,
    connectionStatus,
    meetingData: wsMeetingData,
    serverError,
    meetingForceClosed,
    meetingTimeline,
    // Functions
    sendAudioData,
    disconnect,
    connect,
    sendMessage,
    subscribeToInstance,
    unsubscribeFromInstance,
    setCompletedMeetingData,
    clearWebSocketData,
    getWebSocketState,
    clearServerError
  } = useWebSocket();

  /* ------------------------------------------------------------------
   *  HANDLE WEBSOCKET MEETING DATA UPDATES
   * ----------------------------------------------------------------*/
  useEffect(() => {
    // Handle meeting data received from WebSocket
    if (wsMeetingData) {
      console.log('📋 Meeting data received from WebSocket:', wsMeetingData);
      
      // Enhanced logging for viewer mode
      if (meetingData?.consumerType === 'viewer') {
        console.log('👁️ VIEWER MODE - Detailed meetingData analysis:');
        console.log('📊 Raw wsMeetingData object:', JSON.stringify(wsMeetingData, null, 2));
        // console.log('📊 wsMeetingData keys:', Object.keys(wsMeetingData));
        // console.log('📊 wsMeetingData type:', typeof wsMeetingData);
        // console.log('📊 wsMeetingData is array:', Array.isArray(wsMeetingData));
        
        // Log specific fields that might be of interest
        // if (wsMeetingData.entity) {
        //   console.log('📊 wsMeetingData.entity:', wsMeetingData.entity);
        // }
        // if (wsMeetingData.name) {
        //   console.log('📊 wsMeetingData.name:', wsMeetingData.name);
        // }
        // if (wsMeetingData.short_id) {
        //   console.log('📊 wsMeetingData.short_id:', wsMeetingData.short_id);
        // }
        // if (wsMeetingData.instance_id) {
        //   console.log('📊 wsMeetingData.instance_id:', wsMeetingData.instance_id);
        // }
        // if (wsMeetingData.meetingId) {
        //   console.log('📊 wsMeetingData.meetingId:', wsMeetingData.meetingId);
        // }
        // if (wsMeetingData._id) {
        //   console.log('📊 wsMeetingData._id:', wsMeetingData._id);
        // }
        
        // Log any nested objects
        Object.keys(wsMeetingData).forEach(key => {
          if (typeof wsMeetingData[key] === 'object' && wsMeetingData[key] !== null) {
            console.log(`📊 wsMeetingData.${key} (object):`, wsMeetingData[key]);
          }
        });
      }
      
      // For live recordings, ensure we preserve the meeting name and short_id
      // if they're not already in the WebSocket data
      const updatedMeetingData = {
        ...wsMeetingData,
        // Preserve existing meeting data if WebSocket doesn't provide it
        entity: wsMeetingData.entity || meetingData?.entity,
        name: wsMeetingData.name || meetingData?.name,
        short_id: wsMeetingData.short_id || meetingData?.short_id,
        instance_id: wsMeetingData.instance_id || meetingData?.instance_id
      };
      
      // Update meeting data (this will replace temporary data for Liminal meetings)
      setMeetingData(updatedMeetingData);
    }
  }, [wsMeetingData, meetingData]);

  /* ------------------------------------------------------------------
   *  HANDLE WEBSOCKET SERVER ERRORS
   * ----------------------------------------------------------------*/
  useEffect(() => {
    if (serverError) {
      // Handle special case for remote meeting stop
      if (serverError === 'MEETING_STOPPED_REMOTELY') {
        console.log('🛑 Meeting was stopped remotely by another participant');
        setMeetingStopped(true);
        setIsRecording(false);
        
        // Close WebSocket connection for viewers
        if (meetingData?.consumerType === 'viewer') {
          console.log('🔌 Closing WebSocket connection for viewer');
          disconnect();
        }
        
        // Clear the server error since this is not an actual error
        clearServerError();
        return;
      }
      
      // Don't set coordinatorError for expected audio processing errors
      if (serverError.includes('Whisper API Error') || 
          serverError.includes('audio file could not be decoded') ||
          serverError.includes('format is not supported')) {
        console.warn('Audio processing error (expected):', serverError);
        // Don't change the UI for expected audio processing errors
      } else if (meetingData?.consumerType === 'viewer' && serverError.includes('audio')) {
        // Ignore audio-related errors for viewers since they're not recording
        console.warn('Audio error ignored for viewer mode:', serverError);
      } else {
        setCoordinatorError(serverError);
      }
    }
  }, [serverError, meetingData?.consumerType, disconnect]);

  /* ------------------------------------------------------------------
   *  HANDLE MEETING FORCE CLOSURE
   * ----------------------------------------------------------------*/
  useEffect(() => {
    console.log('🔍 Force closure useEffect triggered, meetingForceClosed:', meetingForceClosed);
    if (meetingForceClosed && meetingData) {
      // Only process force closure if we have current meeting data and the instanceId matches
      if (meetingForceClosed.instanceId === (meetingData?.instance_id || meetingData?.instanceId || meetingData?.id)) {
        console.log('🛑 Meeting was force closed due to duration timeout:', meetingForceClosed);
        
        // Auto-stop the meeting (same behavior as user clicking "Stop Meeting")
        setMeetingStopped(true);
        setIsRecording(false);
        
        // Stop audio capture and transcription through chrome extension (same as manual stop)
        window.postMessage({ type: 'STOP_RECORDING', source: 'react-app' }, '*');
        console.log('📤 Sent STOP_RECORDING message to Chrome extension due to force closure');
        
        // Close WebSocket connection for viewers
        if (meetingData?.consumerType === 'viewer') {
          console.log('🔌 Closing WebSocket connection for viewer due to force closure');
          disconnect();
        }
      } else {
        console.log('⚠️ Ignoring force closure for different meeting instance:', meetingForceClosed.instanceId, 'vs current:', meetingData?.instance_id || meetingData?.instanceId || meetingData?.id);
      }
    }
  }, [meetingForceClosed, meetingData, disconnect]);

  /* ------------------------------------------------------------------
   *  MONITOR CONNECTION STATUS CHANGES
   * ----------------------------------------------------------------*/
  const connectionStatusRef = useRef(null);
  
  useEffect(() => {
    console.log('🔍 Connection status changed:', connectionStatus);
    connectionStatusRef.current = connectionStatus;
  }, [connectionStatus]);

  /* ------------------------------------------------------------------
   *  MONITOR WEBSOCKET STATE
   * ----------------------------------------------------------------*/
  useEffect(() => {
    if (isConnected) {
      console.log('✅ WebSocket is connected');
      
      // Additional logging for viewer mode
      // if (meetingData?.consumerType === 'viewer') {
      //   console.log('👁️ VIEWER MODE - WebSocket connected, waiting for meetingData...');
      // }
    } else {
      console.log('❌ WebSocket is disconnected');
      
      // Additional logging for viewer mode
      // if (meetingData?.consumerType === 'viewer') {
      //   console.log('👁️ VIEWER MODE - WebSocket disconnected');
      // }
    }
  }, [isConnected, meetingData?.consumerType]);



  // Comprehensive cleanup function for all meeting data
  const cleanupMeetingData = () => {
    console.log('🧹 Performing comprehensive meeting data cleanup...');
    
    // Clean up local meeting state
    setMeetingStarted(false);
    setMeetingStopped(false);
    setIsRecording(false);
    setExtensionError(null);
    setCoordinatorError(null);
    setMeetingClosed(false);
    setMeetingObjectives(null);
    setIsStartingNewMeeting(false);
    setIsLoadingMeetingDetails(false);
    setParticipants(null);
    
    // Clear ALL WebSocket data completely using the new function
    clearWebSocketData();
    
    console.log('✅ Meeting data cleanup completed');
  };

  /* ------------------------------------------------------------------
   *  MEETING COORDINATOR HANDLING
   * ----------------------------------------------------------------*/
  const handleMeetingCoordinator = async (config) => {
    console.log('🎯 Handling meeting coordinator config:', config);
    setIsLoading(true);
    setCoordinatorError(null);

    // IMMEDIATELY clear all data before starting any new meeting
    // This ensures no old data is visible during the transition
    setIsStartingNewMeeting(true);
    clearWebSocketData();
    setMeetingData(null);
    setMeetingObjectives(null);
    setParticipants(null);
    setMeetingStarted(false);
    setMeetingStopped(false);
    setIsRecording(false);
    setExtensionError(null);
    setCoordinatorError(null);
    setMeetingClosed(false);

    try {
      switch (config.type) {
        case 'start-liminal':
          await handleStartLiminalMeeting(config.data);
          break;
        
        case 'join-existing':
          await handleJoinExistingMeeting(config.data);
          break;
        
        case 'join-guest':
          await handleJoinGuestMeeting(config.data);
          break;
        
        case 'start-adhoc':
          await handleStartAdhocMeeting(config.data);
          break;
        
        case 'view-completed':
          await handleViewCompletedMeeting(config.data);
          break;
        
        default:
          throw new Error(`Unknown meeting type: ${config.type}`);
      }
    } catch (error) {
      console.error('❌ Meeting coordinator error:', error);
      setCoordinatorError(error.message || 'Failed to process meeting request');
    } finally {
      setIsLoading(false);
      setIsStartingNewMeeting(false);
    }
  };

  const handleStartLiminalMeeting = async (data) => {
    console.log('🎯 Starting Liminal meeting with ID:', data.meetingId);
    
    // For Liminal meetings, set minimal meeting data (validation API not available)
    console.log('📋 Setting minimal meeting data for Liminal meeting');
    setMeetingData({
      _id: data.meetingId,
      entity: { name: `Meeting ${data.meetingId}` },
      meetingId: data.meetingId
    });
    
    // Start the meeting immediately to show the new UI
    setMeetingStarted(true);
    
    await startMeetingWithConfig({
      meetingId: data.meetingId,
      type: 'liminal'
    });
  };

  const handleJoinExistingMeeting = async (data) => {
    console.log('🔗 Joining existing meeting with instance ID:', data.instanceId, 'as', data.role);
    
    // For viewer mode, get instance details from server
    if (data.role === 'viewer') {
      console.log('👁️ Viewer mode - fetching instance details');
      setIsLoadingMeetingDetails(true);
      try {
        // Get instance details from server
        const validationResult = await meetingCoordinatorService.validateInstanceId(data.instanceId);
        if (validationResult.success && validationResult.isValid) {
          console.log('✅ Instance details fetched for viewer:', validationResult.meetingData);
          // Set complete meeting data from server response
          setMeetingData({
            ...validationResult.meetingData,
            consumerType: 'viewer'
          });
        } else {
          console.warn('⚠️ Instance validation failed, using minimal data:', validationResult.message);
          // Fallback to minimal meeting data
          setMeetingData({
            instanceId: data.instanceId,
            entity: { name: `Meeting ${data.instanceId}` },
            meetingId: data.instanceId,
            consumerType: 'viewer'
          });
        }
      } catch (error) {
        console.error('❌ Failed to fetch instance details for viewer:', error);
        // Fallback to minimal meeting data
        setMeetingData({
          instanceId: data.instanceId,
          entity: { name: `Meeting ${data.instanceId}` },
          meetingId: data.instanceId,
          consumerType: 'viewer'
        });
      } finally {
        setIsLoadingMeetingDetails(false);
      }
      // For viewer mode, set meeting started immediately since we don't need to wait for server response
      setMeetingStarted(true);
      await startMeetingWithConfig({
        instanceId: data.instanceId,
        type: 'existing',
        consumerType: 'viewer'
      });
    } else {
      // For recorder mode, set minimal meeting data (validation API not available)
      console.log('📋 Setting minimal meeting data for recorder mode');
      setMeetingData({
        instanceId: data.instanceId,
        entity: { name: `Meeting ${data.instanceId}` },
        meetingId: data.instanceId
      });
      await startMeetingWithConfig({
        instanceId: data.instanceId,
        type: 'existing',
        consumerType: data.role || 'recorder'
      });
    }
  };

  const handleJoinGuestMeeting = async (data) => {
    console.log('👤 Joining as guest with code:', data.guestCode);
    
    // Get guest token from the code
    const tokenResult = await meetingCoordinatorService.getGuestToken(data.guestCode);
    if (!tokenResult.success) {
      throw new Error('Failed to get guest token');
    }

    // Set meeting data and start the meeting
    setMeetingData({ instanceId: tokenResult.instanceId });
    await startMeetingWithConfig({
      instanceId: tokenResult.instanceId,
      type: 'guest'
    });
  };

  const handleStartAdhocMeeting = async (data) => {
    console.log('🚀 Starting ad-hoc meeting:', data.meetingName);
    
    // Start the ad-hoc meeting on the server
    const result = await meetingCoordinatorService.startAdhocMeeting(data);
    if (!result.success) {
      throw new Error('Failed to start ad-hoc meeting');
    }

    // Set meeting data and start the meeting
    setMeetingData(result.meetingData);
    await startMeetingWithConfig({
      instanceId: result.instanceId,
      type: 'adhoc',
      objectives: data.objectives,
      coachingStrategy: data.coachingStrategy
    });
  };

  const handleViewCompletedMeeting = async (data) => {
    console.log('📋 Loading completed meeting:', data.instanceId);
    
    try {
      // Load the completed meeting data
      const result = await meetingCoordinatorService.loadCompletedMeeting(data.instanceId);
      if (!result.success) {
        throw new Error('Failed to load completed meeting');
      }

      console.log('📋 Raw result from loadCompletedMeeting:', result);

      // Set the meeting data from the instance
      setMeetingData({
        ...result.instance,
        consumerType: 'completed',
        isCompleted: true,
        duration: result.instance?.duration !== undefined ? `${Math.floor(result.instance.duration / 60)}:${(result.instance.duration % 60).toString().padStart(2, '0')}` : '0:00',
        participantCount: result.participants?.length || 0,
        entity: {
          name: result.instance?.name || `Meeting ${data.instanceId}`,
          id: result.instance?.instance_id || data.instanceId
        }
      });
      
      // Set meeting objectives - objectives is already an array in the new API structure
      const objectivesArray = result.objectives || [];
      setMeetingObjectives({ objectives: objectivesArray });
      
      // Store participants data for the hover component
      setParticipants(result.participants || []);
      
      // Debug participant data structure
      console.log('🔍 Participant data structure:', result.participants);
      if (result.participants && result.participants.length > 0) {
        console.log('🔍 First participant object:', result.participants[0]);
        console.log('🔍 Available participant fields:', Object.keys(result.participants[0]));
      }
      
      // Debug the data structure before mapping
      console.log('🔍 Data structure analysis:');
      console.log('  - transcriptions:', result.transcriptions);
      console.log('  - objectives:', result.objectives);
      console.log('  - objectivesArray:', objectivesArray);
      console.log('  - suggestions:', result.suggestions);
      console.log('  - suggestions[0]:', result.suggestions?.[0]);
      console.log('  - questions:', result.questions);
      console.log('  - questions[0]:', result.questions?.[0]);
      console.log('  - actionItems:', result.actionItems);
      console.log('  - actionItems[0]:', result.actionItems?.[0]);
      console.log('  - wrapUp:', result.wrapUp);
      console.log('  - wrapUp[0]:', result.wrapUp?.[0]);
      console.log('  - timeline:', result.timeline);
      console.log('  - timeline data structure:', result.timeline?.data);
      
      // Set WebSocket data for display using the new setter
      // Map the data to match the expected format for the components
      const mappedData = {
        transcript: result.transcriptions?.raw || '',
        formatTranscription: result.transcriptions?.formatted || result.transcriptions?.raw || '',
        objectives: {
          objectivesAnalysis: {
            objectives: objectivesArray
          }
        },
        suggestions: {
          nextSteps: {
            nextSteps: Array.isArray(result.suggestions) ? result.suggestions.map(s => {
              // If it's already in the new format, keep it as is
              if (typeof s === 'object' && s.suggestion && s.section) {
                return s;
              }
              // If it's a string, convert to new format
              if (typeof s === 'string') {
                return {
                  suggestion: s,
                  section: 'publish',
                  priority: 'medium',
                  reviewed: false,
                  increment: Date.now() + Math.random()
                };
              }
              // If it's an object but not in new format, try to extract suggestion
              if (typeof s === 'object') {
                return {
                  suggestion: s.suggestions || s.content || s.text || JSON.stringify(s),
                  section: 'publish',
                  priority: 'medium',
                  reviewed: false,
                  increment: Date.now() + Math.random()
                };
              }
              return s;
            }) : []
          }
        },
        questionList: {
          questionsAnalysis: {
            questionsAndConcerns: Array.isArray(result.questions) ? result.questions.map(q => 
              typeof q === 'string' ? { content: q, answer: '', type: 'question' } : 
              typeof q === 'object' ? {
                content: q.question || q.content || q.text || JSON.stringify(q),
                answer: q.content || q.answer || q.text || '',
                type: q.question_type || q.type || 'question'
              } : { content: String(q), answer: '', type: 'question' }
            ) : []
          }
        },
        actionItems: {
          actionItems: Array.isArray(result.actionItems) ? result.actionItems.map(a => 
            typeof a === 'string' ? a : a.action_item || a.content || a.text || JSON.stringify(a)
          ) : []
        },
        emphasisPoints: {
          emphasisPoints: Array.isArray(result.wrapUp) ? result.wrapUp.map(w => 
            typeof w === 'string' ? w : w.wrap_point || w.content || w.text || JSON.stringify(w)
          ) : []
        },
        sessionSentiment: Array.isArray(result.wrapUp) ? result.wrapUp : [],
        timeline: result.timeline || null
      };
      

      
      console.log('📋 Mapped data for setCompletedMeetingData:', mappedData);
      console.log('📊 Timeline data being set:', mappedData.timeline);
      
      setCompletedMeetingData(mappedData);
      
      // Mark as completed meeting (no recording/WebSocket needed)
      setMeetingStarted(true);
      setMeetingStopped(true);
      
      console.log('✅ Completed meeting loaded successfully');
    } catch (error) {
      console.error('❌ Failed to load completed meeting:', error);
      throw error;
    }
  };



  const startMeetingWithConfig = useCallback(async (config) => {
    console.log('🚀 Starting meeting with config:', config);
    console.log('🔄 startMeetingWithConfig function called, isStartingNewMeeting:', isStartingNewMeeting);
    
    // Ensure isStartingNewMeeting is set to false when this function completes
    // This is especially important for viewer mode which doesn't go through the coordinator flow
    // Always reset the starting state when this function completes
    const resetStartingState = () => {
      console.log('🔄 resetStartingState called, isStartingNewMeeting:', isStartingNewMeeting);
      console.log('✅ Always resetting isStartingNewMeeting to false in startMeetingWithConfig');
      setIsStartingNewMeeting(false);
    };
    
    try {
      // Connect to WebSocket with retry logic
      let connected = false;
      let retryCount = 0;
      const maxRetries = 3;
      
      while (!connected && retryCount < maxRetries) {
        try {
          // Check if already connected before attempting to connect
          const currentState = getWebSocketState();
          if (currentState.isOpen) {
            console.log('✅ WebSocket already connected');
            connected = true;
            break;
          }
          
          await connect();
          console.log('✅ WebSocket connected');
          connected = true;
        } catch (error) {
          retryCount++;
          console.warn(`⚠️ WebSocket connection attempt ${retryCount} failed:`, error.message);
          
          if (retryCount < maxRetries) {
            // Wait before retrying
            await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
          } else {
            throw new Error(`Failed to connect to WebSocket after ${maxRetries} attempts: ${error.message}`);
          }
        }
      }
      
      // Wait a moment for the WebSocket to be fully ready
      await new Promise(resolve => setTimeout(resolve, 500));
      console.log('✅ WebSocket ready for use');
      
      // Double-check connection status
      const wsState = getWebSocketState();
      console.log('🔍 WebSocket readyState check:', wsState);
      
      // Ensure WebSocket is actually connected before proceeding
      if (!wsState.isOpen) {
        throw new Error('WebSocket is not in OPEN state after connection attempt');
      }
      
      // Update meeting data with consumer type
      if (meetingData) {
        setMeetingData({
          ...meetingData,
          consumerType: config.consumerType || 'recorder'
        });
      }
      
      // Get the meeting ID for subscription
      const meetingId = config.instanceId || config.meetingId;
      const role = config.consumerType || 'recorder';
      
      // Subscribe to meeting updates using the new pub/sub system
      if (meetingId) {
        try {
          console.log('🔌 Attempting to subscribe to meeting:', meetingId, 'as', role);
          subscribeToInstance(meetingId, role);
          console.log('✅ Subscribed to meeting updates:', meetingId, 'as', role);
          
          // Additional logging for viewer mode
          if (role === 'viewer') {
            console.log('👁️ VIEWER SUBSCRIPTION - Waiting for meetingData from server...');
            console.log('👁️ Current WebSocket state:', getWebSocketState());
          }
        } catch (error) {
          console.error('❌ Failed to subscribe to meeting:', error);
          // Continue anyway - the meeting might still work
        }
      }
      
      // Helper function to ensure start-meeting message is sent
      const ensureStartMeetingMessage = async (config) => {
        const maxAttempts = 5;
        let attempts = 0;
        
        while (attempts < maxAttempts) {
          try {
            const wsState = getWebSocketState();
            if (!wsState.isOpen) {
              console.log(`⏳ Waiting for WebSocket to be ready (attempt ${attempts + 1}/${maxAttempts})`);
              await new Promise(resolve => setTimeout(resolve, 1000));
              attempts++;
              continue;
            }
            
            // Send the appropriate start message based on type
            if (config.type === 'adhoc') {
              sendMessage('start-adhoc-meeting', {
                instanceId: config.instanceId,
                objectives: config.objectives,
                coachingStrategy: config.coachingStrategy
              });
              console.log('✅ start-adhoc-meeting message sent successfully');
              setMeetingStarted(true);
            } else if (config.type === 'liminal') {
              sendMessage('start-meeting', { 
                instance_id: config.meetingId,
                consumerType: config.consumerType || 'recorder',
                clearTranscript: true  // Tell backend to clear transcript data
              });
              console.log('✅ start-meeting message sent successfully for Liminal meeting');
            } else {
              // For existing meetings
              const messageData = {
                instance_id: config.instanceId || config.meetingId,
                consumerType: config.consumerType || 'recorder',
                clearTranscript: true  // Tell backend to clear transcript data
              };
              sendMessage('start-meeting', messageData);
              console.log('✅ start-meeting message sent successfully for existing meeting');
              setMeetingStarted(true);
            }
            
            return; // Success, exit the loop
          } catch (error) {
            attempts++;
            console.warn(`⚠️ Failed to send start message (attempt ${attempts}/${maxAttempts}):`, error.message);
            
            if (attempts >= maxAttempts) {
              throw new Error(`Failed to send start message after ${maxAttempts} attempts: ${error.message}`);
            }
            
            // Wait before retrying
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      };

      // Send appropriate start message based on type
      if (config.consumerType === 'viewer') {
        // For viewer mode, just subscribe - no additional messages needed
        console.log('👁️ Starting in viewer mode - subscription only');
        // Note: meetingStarted is set immediately in the calling code for viewer mode
      } else {
        // For all other meeting types, ensure the start message is sent
        await ensureStartMeetingMessage(config);
      }
      
      // Note: Removed test message to avoid server error
      
      console.log('✅ Meeting configuration sent');
      
      // Reset the starting state when everything is complete
      console.log('🔄 About to call resetStartingState, isStartingNewMeeting:', isStartingNewMeeting);
      resetStartingState();
      console.log('✅ resetStartingState called');
      
    } catch (error) {
      console.error('❌ Failed to start meeting:', error);
      // Reset the starting state even on error
      console.log('🔄 About to call resetStartingState in catch block, isStartingNewMeeting:', isStartingNewMeeting);
      resetStartingState();
      console.log('✅ resetStartingState called in catch block');
      throw new Error('Failed to start meeting session');
    }
  }, [connect, sendMessage, subscribeToInstance, setMeetingStarted, getWebSocketState, meetingData, isStartingNewMeeting]);
  useEffect(() => {
    // Check if we have meeting configuration from the coordinator
    const storedConfig = sessionStorage.getItem('meetingConfig');
    if (storedConfig) {
      try {
        const config = JSON.parse(storedConfig);
        setMeetingConfig(config);
        handleMeetingCoordinator(config);
        // Clear the stored config after processing
        sessionStorage.removeItem('meetingConfig');
      } catch (error) {
        console.error('Error parsing meeting config:', error);
        setCoordinatorError('Invalid meeting configuration');
      }
    }
  }, []);

    /* ------------------------------------------------------------------
   *  ROUTER STATE HANDLING
   * ----------------------------------------------------------------*/
  const stateHandledRef = useRef(false);
  
  useEffect(() => {
    // Check if we have an instance_id from router state
    const instanceId = location.state?.instanceId;
    const meetingDataFromState = location.state?.meetingData;
    const isCompleted = location.state?.isCompleted;
    const joinAsViewer = location.state?.joinAsViewer;
    const guestMode = location.state?.guestMode || (() => {
      try {
        const guestState = sessionStorage.getItem('guestState');
        if (guestState) {
          const parsed = JSON.parse(guestState);
          // Clear the sessionStorage after reading
          sessionStorage.removeItem('guestState');
          return parsed.guestMode;
        }
      } catch (error) {
        console.error('Error reading guest state from sessionStorage:', error);
      }
      return false;
    })();
    
    const singleUserEmail = location.state?.singleUserEmail || (() => {
      try {
        const guestState = sessionStorage.getItem('guestState');
        if (guestState) {
          const parsed = JSON.parse(guestState);
          return parsed.singleUserEmail;
        }
      } catch (error) {
        console.error('Error reading guest state from sessionStorage:', error);
      }
      return null;
    })();
    
    // Check if we have an instance ID from URL parameters (viewer mode)
    
    console.log('🔍 Router state check:', {
      instanceId,
      urlInstanceId,
      meetingDataFromState: !!meetingDataFromState,
      isCompleted,
      joinAsViewer,
      guestMode,
      singleUserEmail,
      meetingStarted,
      meetingConfig: !!meetingConfig,
      stateHandledRef: stateHandledRef.current,
      isStartingNewMeeting
    });
    
    if ((instanceId || urlInstanceId) && !meetingStarted && !meetingConfig && !stateHandledRef.current && !isStartingNewMeeting) {
      // Use URL parameter if available, otherwise use router state
      const finalInstanceId = urlInstanceId || instanceId;
      const isUrlViewerMode = isViewerMode || urlInstanceId;
      
      console.log('🎯 Starting meeting from router state with instance_id:', finalInstanceId, 'isCompleted:', isCompleted, 'joinAsViewer:', joinAsViewer, 'isUrlViewerMode:', isUrlViewerMode);
      console.log('📋 Meeting data from router state:', meetingDataFromState);
      
      // Mark as handled to prevent multiple executions
      stateHandledRef.current = true;
      
      // IMMEDIATELY clear all data before starting the meeting
      // This ensures no old data is visible during the transition
      setIsStartingNewMeeting(true);
      clearWebSocketData();
      setMeetingData(null);
      setMeetingObjectives(null);
      setParticipants(null);
      setMeetingStarted(false);
      setMeetingStopped(false);
      setIsRecording(false);
      setExtensionError(null);
      setCoordinatorError(null);
      setMeetingClosed(false);
      
      if (isCompleted) {
        // Load as completed meeting (no websocket)
        handleViewCompletedMeeting({ instanceId: finalInstanceId }).finally(() => {
          setIsStartingNewMeeting(false);
        });
      } else if (guestMode) {
        // Guest mode - join as viewer with guest token
        console.log('👤 Joining meeting as guest');
        
        // Fetch instance details for guest mode
        const fetchGuestDetails = async () => {
          setIsLoadingMeetingDetails(true);
          try {
            console.log('👤 Guest mode - fetching instance details');
            const validationResult = await meetingCoordinatorService.validateInstanceId(finalInstanceId);
            if (validationResult.success && validationResult.isValid) {
              console.log('✅ Instance details fetched for guest:', validationResult.meetingData);
              // Set complete meeting data from server response with guest info
              setMeetingData({
                ...validationResult.meetingData,
                consumerType: 'viewer',
                guestMode: true,
                singleUserEmail: singleUserEmail
              });
            } else {
              console.warn('⚠️ Instance validation failed for guest, using minimal data:', validationResult.message);
              // Fallback to minimal meeting data
              setMeetingData({
                instanceId: finalInstanceId,
                entity: { name: `Meeting ${finalInstanceId}` },
                meetingId: finalInstanceId,
                consumerType: 'viewer',
                guestMode: true,
                singleUserEmail: singleUserEmail
              });
            }
          } catch (error) {
            console.error('❌ Failed to fetch instance details for guest:', error);
            // Fallback to minimal meeting data
            setMeetingData({
              instanceId: finalInstanceId,
              entity: { name: `Meeting ${finalInstanceId}` },
              meetingId: finalInstanceId,
              consumerType: 'viewer',
              guestMode: true,
              singleUserEmail: singleUserEmail
            });
          } finally {
            setIsLoadingMeetingDetails(false);
          }
          
          // For guest mode, set meeting started immediately
          setMeetingStarted(true);
          // Start the meeting with the instance_id as viewer
          startMeetingWithConfig({
            instanceId: finalInstanceId,
            type: 'existing',
            consumerType: 'viewer'
          }).catch(error => {
            console.error('❌ Error in startMeetingWithConfig for guest:', error);
          });
        };
        
        // Execute the async function
        fetchGuestDetails();
      } else if (joinAsViewer || isUrlViewerMode) {
        // Join as viewer (either from router state or URL parameter)
        console.log('👁️ Joining meeting as viewer');
        
        // Fetch instance details for viewer mode
        const fetchViewerDetails = async () => {
          setIsLoadingMeetingDetails(true);
          try {
            console.log('👁️ Viewer mode - fetching instance details from URL');
            const validationResult = await meetingCoordinatorService.validateInstanceId(finalInstanceId);
            if (validationResult.success && validationResult.isValid) {
              console.log('✅ Instance details fetched for viewer from URL:', validationResult.meetingData);
              // Set complete meeting data from server response
              setMeetingData({
                ...validationResult.meetingData,
                consumerType: 'viewer'
              });
            } else {
              console.warn('⚠️ Instance validation failed for URL viewer, using minimal data:', validationResult.message);
              // Fallback to minimal meeting data
              setMeetingData({
                instanceId: finalInstanceId,
                entity: { name: `Meeting ${finalInstanceId}` },
                meetingId: finalInstanceId,
                consumerType: 'viewer'
              });
            }
          } catch (error) {
            console.error('❌ Failed to fetch instance details for URL viewer:', error);
            // Fallback to minimal meeting data
            setMeetingData({
              instanceId: finalInstanceId,
              entity: { name: `Meeting ${finalInstanceId}` },
              meetingId: finalInstanceId,
              consumerType: 'viewer'
            });
          } finally {
            setIsLoadingMeetingDetails(false);
          }
          
          // For viewer mode, set meeting started immediately since we don't need to wait for server response
          setMeetingStarted(true);
          // Start the meeting with the instance_id as viewer
          startMeetingWithConfig({
            instanceId: finalInstanceId,
            type: 'existing',
            consumerType: 'viewer'
          }).catch(error => {
            console.error('❌ Error in startMeetingWithConfig for viewer:', error);
          });
        };
        
        // Execute the async function
        fetchViewerDetails();
      } else {
        // For recorder mode, use the meeting data from router state if available
        if (meetingDataFromState) {
          console.log('📋 Using meeting data from router state:', meetingDataFromState);
          console.log('📋 Available fields in meeting data:', Object.keys(meetingDataFromState));
          // Transform the meeting data to match the expected format
          const transformedMeetingData = {
            _id: meetingDataFromState.instance_id,
            instanceId: meetingDataFromState.instance_id,
            entity: { 
              name: meetingDataFromState.name,
              id: meetingDataFromState.instance_id
            },
            name: meetingDataFromState.name,
            short_id: meetingDataFromState.short_id,
            description: meetingDataFromState.description,
            meetingId: meetingDataFromState.instance_id,
            training_id: meetingDataFromState.training_id,
            consumerType: 'recorder'  // ← ADD THIS LINE
          };
          setMeetingData(transformedMeetingData);
        } else {
          // Fallback to minimal data if no meeting data available
          setMeetingData({
            instanceId: finalInstanceId,
            entity: { name: `Meeting ${finalInstanceId}` },
            meetingId: finalInstanceId
          });
        }
        
        // Set meeting started immediately so the UI renders
        setMeetingStarted(true);
        console.log('✅ Meeting started state set to true from router state');
        
        // Set isStartingNewMeeting to false immediately so the UI renders
        setIsStartingNewMeeting(false);
        console.log('✅ isStartingNewMeeting set to false');
        
        // Start the meeting with the instance_id
        startMeetingWithConfig({
          instanceId: finalInstanceId,
          type: 'existing',
          consumerType: 'recorder'
        }).catch(error => {
          console.error('❌ Error in startMeetingWithConfig:', error);
        });
      }
    }
  }, [location.state, meetingStarted, meetingConfig, startMeetingWithConfig, urlInstanceId, isViewerMode, isStartingNewMeeting]);



  /* ------------------------------------------------------------------
   *  DEBUG LOGGING FOR TRAINING
   * ----------------------------------------------------------------*/
  useEffect(() => {
    if (meetingData) {
      console.log('🔍 Meeting data loaded:', {
        instanceId: meetingData.instanceId,
        name: meetingData.name,
        training_id: meetingData.training_id,
        hasTrainingId: !!meetingData.training_id,
        trainingIdType: typeof meetingData.training_id,
        consumerType: meetingData.consumerType,
        isCompleted: meetingData.consumerType === 'completed',
        isViewer: meetingData.consumerType === 'viewer',
        shouldShowTraining: !!(
          meetingData.training_id && 
          typeof meetingData.training_id === 'number' && 
          meetingData.training_id > 0 && 
          meetingData.consumerType === 'recorder'
        )
      });
    }
  }, [meetingData]);

  /* ------------------------------------------------------------------
   *  MESSAGE HANDLING FROM EXTENSION
   * ----------------------------------------------------------------*/
  useEffect(() => {
    function handleMessage(event) {
      // Only handle messages from our extension
      if (event.source !== window) return;
      if (!event.data || typeof event.data !== 'object') return;

      // Only log non-audio messages to reduce noise
      // if (event.data.type !== 'RAW_AUDIO_CHUNK') {
      //   console.log('📨 Received message from extension:', event.data);
      // }

      switch (event.data.type) {
        case 'RECORDING_STARTED':
          console.log('🎙️ Recording started');
          setIsRecording(true);
          setExtensionError(null);
          break;

        case 'RECORDING_STOPPED':
          console.log('🛑 Recording stopped');
          setIsRecording(false);
          break;

        case 'RECORDING_STATE':
          console.log('🎙️ Recording state changed:', event.data.isRecording);
          setIsRecording(event.data.isRecording);
          if (event.data.isRecording) {
            setExtensionError(null);
          }
          break;

        case 'RAW_AUDIO_CHUNK':
          // Forward audio data to WebSocket (only for recorder mode)
          if (meetingData?.consumerType === 'viewer') {
            console.log('👁️ Viewer mode - ignoring audio chunk');
            break;
          }
          
          const audioData = event.data.buf || event.data.audioData;
          
          // Don't send audio data if meeting is stopped
          if (meetingStopped) {
            console.log('⚠️ Meeting is stopped, ignoring audio chunk');
            break;
          }
          
          if (isConnected && audioData) {
            try {
              sendAudioData(audioData);
              // Single consolidated log message for audio processing
              console.log('🎵 Audio chunk processed:', (audioData.byteLength || audioData.length) + ' bytes');
            } catch (error) {
              console.error('❌ Failed to send audio data:', error);
              setExtensionError('Failed to send audio data to server');
            }
          } else {
            console.log('⚠️ Cannot send audio data - not connected or no data');
          }
          break;

        case 'EXTENSION_ERROR':
          console.error('❌ Extension error:', event.data.error);
          setExtensionError(event.data.error);
          break;

        case 'STOP_RECORDING':
          console.log('🛑 Stop recording message received from extension');
          // This is just a confirmation that the extension received the stop command
          // The actual recording state change will come via RECORDING_STATE
          break;

        default:
          // Filter out webpack development messages to reduce console noise
          if (event.data.type && !event.data.type.startsWith('webpack')) {
            console.log('📨 Unknown message type:', event.data.type);
          }
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isConnected, sendAudioData, meetingStopped]);

  /* ------------------------------------------------------------------
   *  MEETING HANDLERS
   * ----------------------------------------------------------------*/
  const handleMeetingLoaded = (data) => {
    console.log('📋 Meeting loaded:', data);
    setMeetingData(data);
  };

  const handleConclusionGenerated = (conclusionData) => {
    console.log('📝 Conclusion generated:', conclusionData);
    // This will now be handled by WebSocket data
  };

  const handleStartMeeting = async (data) => {
    console.log('🚀 handleStartMeeting called with data:', data);
    console.log('📊 Current state before start:', {
      meetingData: !!meetingData,
      meetingStarted,
      isConnected
    });
    
    // IMMEDIATELY clear all data before starting the meeting
    // This ensures no old data is visible during the transition
    setIsStartingNewMeeting(true);
    clearWebSocketData();
    setMeetingData(null);
    setMeetingObjectives(null);
    setParticipants(null);
    setMeetingStarted(false);
    setMeetingStopped(false);
    setIsRecording(false);
    setExtensionError(null);
    setCoordinatorError(null);
    setMeetingClosed(false);
    
    // Set meeting data if provided
    if (data) {
      setMeetingData(data);
      console.log('📋 Meeting data set');
    }
    
    const meeting = data || meetingData;
    if (!meeting) {
      console.log('❌ No meeting data available');
      return;
    }

    try {
      console.log('🔌 Connecting to WebSocket...');
      // Start WebSocket connection
      await connect();
      console.log('✅ WebSocket connected');
      
      // Send start-meeting message to server
      console.log('📤 Sending start-meeting message to server');
      const meetingId = meeting._id || meeting.entity?.id || meeting.id || meeting.meetingId;
      sendMessage('start-meeting', { 
        value: true,
        instance_id: meetingId,
        consumerType: 'recorder'
      });
      
      // Start meeting state
      setMeetingStarted(true);
      console.log('✅ Meeting started state set to true');
      
      console.log('📊 State after start:', {
        meetingData: !!meeting,
        meetingStarted: true,
        isConnected: true
      });
      
    } catch (error) {
      console.error('❌ Failed to start meeting:', error);
      setExtensionError('Failed to start meeting. Please try again.');
    } finally {
      setIsStartingNewMeeting(false);
    }
  };

  /* ------------------------------------------------------------------
   *  UI ACTIONS
   * ----------------------------------------------------------------*/
  const handleCopyMeetingInvite = async () => {
    // Get the instance ID from meeting data
    const instanceId = meetingData?.instanceId || 
                      meetingData?.instance_id || 
                      meetingData?._id || 
                      meetingData?.meetingId;
    
    if (!instanceId) {
      console.error('❌ No instance ID available for meeting invite');
      return;
    }
    
    // Get the base URL from the current app location (frontend URL)
    const baseUrl = window.location.origin || 'http://localhost:3000';
    
    // Construct the full meeting invite URL
    const inviteUrl = `${baseUrl}/meeting/${instanceId}`;
    
    try {
      // Copy to clipboard
      await navigator.clipboard.writeText(inviteUrl);
      console.log('✅ Meeting invite URL copied to clipboard:', inviteUrl);
      
      // Show success message
      setShowCopiedMessage(true);
      setTimeout(() => setShowCopiedMessage(false), 2000); // Hide after 2 seconds
    } catch (error) {
      console.error('❌ Failed to copy meeting invite URL:', error);
      
      // Fallback for older browsers or when clipboard API is not available
      try {
        const textArea = document.createElement('textarea');
        textArea.value = inviteUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        console.log('✅ Meeting invite URL copied to clipboard (fallback):', inviteUrl);
        
        // Show success message for fallback too
        setShowCopiedMessage(true);
        setTimeout(() => setShowCopiedMessage(false), 2000); // Hide after 2 seconds
      } catch (fallbackError) {
        console.error('❌ Failed to copy meeting invite URL (fallback):', fallbackError);
      }
    }
  };

  const handleStopMeeting = () => {
    console.log('🛑 Stop Meeting clicked');
    setMeetingStopped(true);
    setIsRecording(false);
    
    // Stop audio capture and transcription through chrome extension
    window.postMessage({ type: 'STOP_RECORDING', source: 'react-app' }, '*');
    console.log('📤 Sent STOP_RECORDING message to Chrome extension');
    
    // Keep WebSocket connection open for conclusion generation
    // Just stop the recording and audio processing
    console.log('🔌 WebSocket connection kept open for conclusion generation');
  };

  const handleCloseMeeting = () => {
    console.log('🔒 Close Meeting clicked');
    setMeetingClosed(true);
    
    // Unsubscribe from meeting updates
    if (isConnected) {
      try {
        unsubscribeFromInstance();
        console.log('✅ Unsubscribed from meeting updates');
      } catch (error) {
        console.warn('⚠️ Failed to unsubscribe from meeting:', error);
      }
    }
    
    // Send stop-meeting message to server (only for active meetings)
    if (isConnected && !meetingData?.isCompleted) {
      console.log('📤 Sending stop-meeting message to server');
      const meetingId = meetingData?._id || meetingData?.entity?.id || meetingData?.id || meetingData?.meetingId;
      sendMessage('stop-meeting', { 
        value: true,
        meetingId: meetingId
      });
    }
    
    // Close the WebSocket connection
    if (isConnected) {
      console.log('🔌 Closing WebSocket connection');
      disconnect();
    }
    
    // Clear meeting state
    setMeetingData(null);
    setMeetingObjectives(null);
    setParticipants(null);
    setMeetingStarted(false);
    setMeetingStopped(false);
    setIsRecording(false);
    setExtensionError(null);
    setCoordinatorError(null);
    
    // Clear ALL WebSocket data including transcript data
    clearWebSocketData();
    
    // Additional explicit clearing to ensure transcript data is gone
    console.log('🧹 Explicitly clearing all meeting data on close');
    
    // Navigate back to home page
    navigate('/');
  };

  const handleSharing = () => {
    console.log('👥 Sharing clicked');
    setShowSharingPanel(true);
  };

  const handleTimeline = () => {
    console.log('📊 Timeline clicked');
    setShowTimelinePanel(true);
  };

  /* ------------------------------------------------------------------
   *  RENDER
   * ----------------------------------------------------------------*/
  
  // Show loading state for coordinator operations, when starting a new meeting, or when fetching meeting details
  if (isLoading || isStartingNewMeeting || isLoadingMeetingDetails) {
    console.log('🔄 Showing loading state - isLoading:', isLoading, 'isStartingNewMeeting:', isStartingNewMeeting, 'isLoadingMeetingDetails:', isLoadingMeetingDetails);
    return (
      <div className="App">
        <header className="App-header">
          <img alt="Liminal" src={linkLogo} className="logo" />
          <h1>Liminal Coaching Agent</h1>
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>
              {isLoading ? 'Setting up your meeting...' : 
               isStartingNewMeeting ? 'Starting new meeting...' : 
               'Loading meeting details...'}
            </p>
          </div>
        </header>
      </div>
    );
  }

  // Show coordinator error
  if (coordinatorError) {
    return (
      <div className="App">
        <header className="App-header">
          <img alt="Liminal" src={linkLogo} className="logo" />
          <h1>Liminal Coaching Agent</h1>
          <div className="error-container">
            <div className="error-message">
              <p>⚠️ {coordinatorError}</p>
              <button onClick={() => navigate('/')}>Back to Home</button>
            </div>
          </div>
        </header>
      </div>
    );
  }
  
  // Show LoadMeeting component if no meeting is loaded and no coordinator config
  if (!meetingData && !meetingConfig) {
    return (
      <div className="App">
        <header className="App-header">
          <img alt="Liminal" src={linkLogo} className="logo" />
          <h1>Liminal Coaching Agent</h1>
          <LoadMeeting onMeetingLoaded={handleMeetingLoaded} onStartMeeting={handleStartMeeting} />
        </header>
      </div>
    );
  }

  // Show the meeting interface when meeting is loaded
  console.log('🎯 Rendering meeting interface with meetingData:', meetingData);
  console.log('🔍 Force closure debug - meetingForceClosed:', meetingForceClosed);
  return (
    <div className="App">
        <header className="App-header">
        <div className="meeting-header">
          <div className="breadcrumb">
            <i className="ti ti-home"></i>
            <i className="ti ti-chevron-right"></i>
            <span>Meetings</span>
            <i className="ti ti-chevron-right"></i>
            <span>Coach</span>
            <i className="ti ti-chevron-right"></i>
            <span>
              {(() => {
                const displayName = getDisplayName(meetingData);
                // console.log('🎯 Rendering display name:', displayName);
                return displayName;
              })()}
            </span>
          </div>
          <div className="session-type">
            <span 
              className={`session-type-badge ${meetingData?.consumerType === 'viewer' ? 'viewer-badge' : ''} ${meetingData?.consumerType === 'completed' ? 'completed-badge' : ''} ${!meetingData?.isCompleted ? 'clickable-badge' : ''}`}
              onClick={!meetingData?.isCompleted ? handleCopyMeetingInvite : undefined}
              title={!meetingData?.isCompleted ? 'Click to copy meeting invite URL' : undefined}
            >
              {meetingData?.consumerType === 'completed' ? 'Completed' : (meetingData?.consumerType || 'recorder')}
            </span>
            {showCopiedMessage && (
              <span className="copied-message">
                ✓ Copied!
              </span>
            )}
          </div>
          {meetingData && (
            <div className="meeting-status">
              {/* Show meeting duration and participant count for completed meetings */}
              {meetingData?.isCompleted && (
                <div className="meeting-info">
                  <span className="meeting-duration">
                    <i className="ti ti-clock"></i>
                    {meetingData.duration || '0:00'}
                  </span>
                  <ParticipantHover 
                    participants={participants}
                    participantCount={meetingData.participantCount || 0}
                  />
                </div>
              )}
              
              {/* Show force closure badge */}
              {meetingForceClosed && (
                <span className="force-closed-badge">
                  ⏰ Meeting Automatically Closed - Expected Duration Exceeded
                </span>
              )}
              
              {/* Show sharing button for all meeting types, connection status only for live meetings */}
              {/* Timeline icon */}
              <button 
                onClick={handleTimeline}
                className="timeline-icon"
                title="View meeting timeline"
              >
                <i className="ti ti-timeline"></i>
              </button>
              
              {/* User icon for sharing */}
              <button 
                onClick={handleSharing}
                className="sharing-user-icon"
                title="Manage meeting sharing"
              >
                <i className="ti ti-users"></i>
              </button>
              
              {/* Only show connection status for live meetings, not completed meetings */}
              {!meetingData?.isCompleted && (
                <span className={`connection-status ${meetingStopped ? 'stopped' : (isConnected ? 'connected' : 'disconnected')}`}>
                  {meetingStopped ? '🟡 Meeting Stopped' : (isConnected ? '🟢 Connected' : '🔴 Disconnected')}
                </span>
              )}
              
              {/* Header buttons - different for each mode */}
              <div className="header-buttons">
                {meetingData?.consumerType === 'viewer' ? (
                  <button 
                    onClick={() => navigate('/')}
                    className="close-meeting-button"
                  >
                    Close Viewer
                  </button>
                ) : meetingData?.isCompleted ? (
                  <button 
                    onClick={handleCloseMeeting}
                    className="close-meeting-button"
                  >
                    Close Meeting
                  </button>
                ) : (
                  <>
                    {!meetingStopped ? (
                      <button 
                        onClick={handleStopMeeting}
                        disabled={!isRecording && !isConnected}
                        className="stop-meeting-button"
                      >
                        Stop Meeting
                      </button>
                    ) : (
                      <button 
                        onClick={handleCloseMeeting}
                        className="close-meeting-button"
                      >
                        Close Meeting
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {extensionError && (
          <div className="error-message">
            <p>⚠️ {extensionError}</p>
            <button onClick={() => window.location.reload()}>Reload Page</button>
          </div>
        )}

        {meetingStopped && meetingData?.consumerType === 'viewer' && (
          <div className="meeting-status-message">
            <p>📺 The meeting has ended. You can continue to view the transcript and insights, or close the viewer.</p>
          </div>
        )}







        {/* Show three-column layout when meeting is started */}
        {meetingStarted ? (
          <div className={`meeting-layout meeting-experience ${meetingData?.training_id && typeof meetingData.training_id === 'number' && meetingData.training_id > 0 && meetingData.consumerType === 'recorder' ? 'with-training' : ''}`}>
            {/* Column 1: Transcript Tabs */}
            <div className="transcript-column">
              <TranscriptTabs
                formattedTranscript={formatTranscription}
                rawTranscript={rawTranscription}
                interimTranscription={interimTranscription}
                isRecording={isRecording}
                isLive={meetingData?.consumerType === 'viewer' ? isConnected && !meetingData?.isCompleted : isRecording}
              />
            </div>

            {/* Column 2: Meeting Objectives */}
            <div className="insights-column">
              <MeetingObjectives
                updatedObjectives={objectives}
                meetingObjectives={meetingObjectives}
              />
            </div>

            {/* Column 3: Meeting Insights */}
            <div className="insights-column">
              <MeetingInsights
                nextSteps={suggestions}
                updatedObjectives={objectives}
                conclusionData={sessionSentiment}
                meetingData={meetingData}
                rawTranscript={rawTranscription}
                questionsAnswers={questionList}
                actionItems={actionItems}
                emphasisPoints={emphasisPoints}
                onConclusionGenerated={handleConclusionGenerated}
              />
            </div>
          </div>
        ) : (
          /* Show loading state when meeting is loaded but not started */
          <div className="meeting-loading-state">
            <div className="loading-spinner"></div>
            <p>Starting meeting...</p>
          </div>
        )}
      </header>
      
      {/* Sharing Panel */}
      <SharingPanel
        isOpen={showSharingPanel}
        onClose={() => setShowSharingPanel(false)}
        instanceId={meetingData?.instance_id || meetingData?.entity?.id || meetingData?.id || meetingData?.meetingId}
        meetingName={meetingData?.name || meetingData?.entity?.name}
        meetingData={meetingData}
      />
      
      {/* Timeline Panel */}
      <TimelinePanel
        isOpen={showTimelinePanel}
        onClose={() => setShowTimelinePanel(false)}
        timelineData={meetingTimeline}
      />
      
      {/* Training Player */}
      {meetingData?.training_id && 
       typeof meetingData.training_id === 'number' && 
       meetingData.training_id > 0 && 
       meetingData.consumerType === 'recorder' && (
        <TrainingPlayer 
          trainingId={meetingData.training_id}
          isConnected={isConnected}
          sendAudioData={sendAudioData}
          meetingStopped={meetingStopped}
        />
      )}

    </div>
  );
}

export default Meeting; 